<script setup lang="ts">
import { ButtonComp, ContentComp, InputBool, InputInt, WizardComp } from '@/components';
import { useLoaders, useTaskLoader } from '@/composeables';
import { CategoryDao, EventDao, GroupDao } from '@/dao';
import { BibAssignSaveRequest, BibAssignSort, BlockedBib, Category, Eventinfo } from '@/objects/generated';
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import { useContestantStore, useEventStore, useToastStore } from '@/stores';
import { ref } from 'vue';
import BibAssignSortPage from './BibAssignSortPage.vue';
import BibAssignBaseTableView from './BibAssignBaseTableView.vue';
import { BaseObjDao, BibAssignDao } from '@/dao';
import { CategoryWithBibAssignFields, GroupWithBibAssignFields } from './BibAssignBaseTableRow';
import { featureIcons } from '@/components/Menu/featureicons';

const eventStore = useEventStore();

const { AddLoader, LoadState } = useTaskLoader();

const props = withDefaults(
    defineProps<{
        /**
         * If true, we will skip the wizard part and open the table only and show more warnings
         * This is used when the bibs are already assigned and we just want to show the table.
         * @default false
         */
        hasBibAssignRules?: boolean | null;
    }>(),
    {
        hasBibAssignRules: null,
    },
);

const emit = defineEmits<{
    (e: 'closed'): void;

    /**
     * This is emitted every time the tabs change, but will only be true if the user have filled all neccessary fields
     * and the bibs can be assigned.
     */
    (e: 'assignable', canAssign: boolean): void;

    /**
     * This is emitted when the table page is selected
     */
    (e: 'isTablePage', state: boolean): void;
    /**
     * This is emitted when the seeding page is selected.
     */
    (e: 'isSeedingPage', state: boolean): void;

    /**
     * This is emitted when the hasChanges state changes.
     */
    (e: 'hasChanges', state: boolean): void;

    /**
     * This is emitted when the bibs have been assigned.
     */
    (e: 'assigned'): void;

    /**
     * This is emitted when the user leaves a page in the wizard.
     */
    (e: 'leftPage', page: string): void;
}>();

const wizardDefinition = new WizardContainer();
wizardDefinition.WizardPages = [
    new WizardPageDefinition('categorygroupselect', ''),

    // new WizardPageDefinition('assignreservebibs', ''), // TODO: We need to agree on how to assign reserve bibs. Should we just create a big range of reserve bibs and assign them to categories/groups?
    new WizardPageDefinition('basetable', ''),
];

const wizard = ref<InstanceType<typeof WizardComp> | null>(null);

/** Model for event */
const event = ref<Eventinfo | null>(null);
/** Model for sort */
const bibAssignSort = ref<Array<BibAssignSort>>([]);
/** Model for categories */
const categories = ref<Array<CategoryWithBibAssignFields>>([]);
/** Model for groups */
const groups = ref<Array<GroupWithBibAssignFields>>([]);
/** Model for blocked bibs */
const blockedBibs = ref<Array<BlockedBib>>([]);

const numberOfReserveBibsPrCategory = ref(0);
const numberOfReserveBibsPrGroup = ref(0);
const assignReserveBibsBefore = ref(false);

/**
 * This will save all the settings for the bib assignment. But it will not assign them to the bibs yet.
 */
const save = async () => {
    await AddLoader(async () => {
        if (event.value == null) {
            return;
        }

        const req = BibAssignSaveRequest.FromJsValues({
            EventInfo: event.value!,
            Sorts: bibAssignSort.value,
            Categories: categories.value.map((c) => c.BackingValue),
            Groups: groups.value.map((g) => g.BackingValue),
            BlockedBibs: blockedBibs.value,
        });

        const resp = await BibAssignDao.SaveBibAssign(event.value!.ID, req);

        // reload saved
        await Promise.all([
            reloadChangedCategories(resp?.CategoryIds ?? []),
            reloadChangedGroups(resp?.GroupIds ?? []),
            reloadChangedSorts(resp?.SortIds ?? []),
            reloadChangedBlockedBibs(resp?.BlockedBibIds ?? []),
        ]);

        if (!resp?.Success) {
            useToastStore().addError('bibassign.settingsnotsaved');
        }
    }, '');
};

/**
 * Define if we should assign bibs by category (true) or by group (false).
 * This will set the event's CategoryControlled property accordingly and move to the next step in the wizard.
 * @param byCategory
 */
const assignByCategory = (byCategory: boolean) => {
    if (event.value == null || event.value.Setup == null) {
        return;
    }
    event.value.Setup.CategoryControlled = byCategory;

    // tell outside we left the page
    emit('leftPage', wizard.value?.currentPageDefinition?.Name ?? '');

    // Move to the next step in the wizard
    wizard.value?.next();
};

/**
 * Assign reserve bibs for categories and groups.
 */
const assignReserveBibsForCategoriesAndGroups = () => {
    if (numberOfReserveBibsPrCategory.value > 0 && event.value?.Setup?.CategoryControlled) {
        // We loop through the categories and set all that dont have a reserve bib to the number of reserve bibs pr category
        categories.value.forEach((c) => {
            if (c.BackingValue.ReserveBibs == 0) {
                c.BackingValue.ReserveBibs = numberOfReserveBibsPrCategory.value;
                c.BackingValue.ReserveBibsInFront = assignReserveBibsBefore.value;
            }
        });
    } else if (numberOfReserveBibsPrGroup.value > 0 && !event.value?.Setup?.CategoryControlled) {
        // We loop through the groups and set all that dont have a reserve bib to the number of reserve bibs pr group
        groups.value.forEach((g) => {
            if (g.BackingValue.ReserveBibs == 0) {
                g.BackingValue.ReserveBibs = numberOfReserveBibsPrGroup.value;
                g.BackingValue.ReserveBibsInFront = assignReserveBibsBefore.value;
            }
        });
    }

    wizard.value?.next();
};

const reloadChangedSorts = async (uids: number[]) => {
    if (uids.length == 0) {
        return;
    }
    const reloaded = await BaseObjDao.Load<BibAssignSort>(BibAssignSort, eventStore.eventID, uids);
    const notchanged = bibAssignSort.value.filter((c) => c.EntityState == 'Unchanged' && !uids.includes(c.ID));
    bibAssignSort.value = [...reloaded, ...notchanged];
};

const reloadChangedBlockedBibs = async (uids: number[]) => {
    if (uids.length == 0) {
        return;
    }
    const reloaded = await BaseObjDao.Load<BlockedBib>(BlockedBib, eventStore.eventID, uids);
    const notchanged = blockedBibs.value.filter((c) => c.EntityState == 'Unchanged' && !uids.includes(c.Bib));
    blockedBibs.value = [...reloaded, ...notchanged];
};

const reloadChangedCategories = async (uids: number[]) => {
    if (uids.length == 0) {
        return;
    }
    const reloaded = await BaseObjDao.Load(Category, eventStore.eventID, uids);
    const notchanged = categories.value.filter((c) => c.EntityState == 'Unchanged' && !uids.includes(c.BackingValue.ID));
    categories.value = [
        ...reloaded.map((x) => {
            const store = useContestantStore();
            const numContestants = store.GetCategoryContestants(x.ID, {
                visibleOnly: true,
            }).length;
            return new CategoryWithBibAssignFields(x, numContestants);
        }),
        ...notchanged,
    ];
};
const reloadChangedGroups = async (uids: number[]) => {
    if (uids.length == 0) {
        return;
    }
    const reloaded = await GroupDao.GroupsWithCount(eventStore.eventID, uids);
    const notchanged = groups.value.filter((c) => c.EntityState == 'Unchanged' && !uids.includes(c.BackingValue.ID));
    groups.value = [
        ...reloaded.map((x) => {
            const numContestants = x.NumContestants ?? 0; // TODO: When we remove the DTO GroupWithCount we fix here
            return new GroupWithBibAssignFields(x, numContestants);
        }),
        ...notchanged,
    ];
};

AddLoader(async () => {
    event.value = await EventDao.Event(eventStore.eventID);
}, 'bibassign.loadevent');

AddLoader(async () => {
    bibAssignSort.value = await BaseObjDao.Load<BibAssignSort>(BibAssignSort, eventStore.eventID);
}, 'bibassign.loadsort');

AddLoader(async () => {
    categories.value = (await BaseObjDao.Load(Category, eventStore.eventID)).map((x) => {
        const store = useContestantStore();
        const numContestants = store
            .GetCategoryContestants(x.ID)
            .filter((c) => store.VisibleContestantStatuses.includes(c.ContestantStatusID)).length;
        return new CategoryWithBibAssignFields(x, numContestants);
    });
}, 'category.loadingcategories');

AddLoader(async () => {
    groups.value = (await GroupDao.GroupsWithCount(eventStore.eventID)).map((x) => {
        const numContestants = x.NumContestants ?? 0; // TODO: When we remove the DTO GroupWithCount we fix here
        return new GroupWithBibAssignFields(x, numContestants);
    });
}, 'group.loadingGroups');

AddLoader(async () => {
    blockedBibs.value = await BaseObjDao.Load<BlockedBib>(BlockedBib, eventStore.eventID);
}, 'bibassign.loadblocked');

useLoaders(AddLoader, ['CATEGORIES', 'RACES', 'GROUPS', 'CONTESTANTS']);

const sortPage = ref<InstanceType<typeof BibAssignSortPage> | null>(null);
const baseTablePage = ref<InstanceType<typeof BibAssignBaseTableView> | null>(null);

let hasSetDefaultPage = false;

const tabShown = (tab: WizardPageDefinition) => {
    if (tab.Name == 'seeding') {
        sortPage.value?.shown();
    }
    if (tab.Name == 'basetable') {
        baseTablePage.value?.shown();
    }
    lastPage.value = tab.Name == 'complete';
    firstPage.value = tab.Name == 'general';

    if (!hasSetDefaultPage && props.hasBibAssignRules != null && props.hasBibAssignRules) {
        // If we have bib assign rules, we should start on the last page
        wizard.value?.goto(wizardDefinition.WizardPages[1]);
        hasSetDefaultPage = true;
    }
};

const lastPage = ref<boolean>(false);
const firstPage = ref<boolean>(false);

defineExpose({
    /**
     * This will move to the next page in the wizard.
     */
    next: () => wizard.value?.next(),

    /**
     * This will move to the previous page in the wizard.
     */
    previous: () => wizard.value?.previous(),

    /**
     * This will save the current settings and optionally close the wizard.
     * @param closeAfterSave - If true, the wizard will be closed after saving.
     */
    save: save,

    /**
     * This is set if we are on the last page of the wizard.
     */
    lastPage,

    /**
     * This is set if we are on the first page of the wizard.
     */
    firstPage,

    /**
     * This will open the table page in the wizard.
     */
    gotoTable: () => baseTablePage.value?.gotoTable(),

    /**
     * This will open the seeding page in the wizard.
     */
    gotoSeeding: () => baseTablePage.value?.gotoSeeding(),

    /**
     * Will build and show the preview table with the current settings.
     */
    showPreview: () => baseTablePage.value?.showPreview(),

    /**
     * Will build and show the preview with graphics.
     */
    printPreviewReport: () => baseTablePage.value?.printPreviewReport(),

    /**
     * This will open the assign dialog to ask for confirmation on assigning bibs.
     */
    openAssignDialog: () => baseTablePage.value?.openAssignDialog(),

    goto: (page: string) => {
        // is it in main wizard pages?
        const mainpage = wizardDefinition.WizardPages.find((p) => p.Name == page);
        if (mainpage != null) {
            wizard.value?.goto(mainpage);
            return;
        }
        // if we are not on the page with the inner wizard we need to go there first
        const settingsPage = wizardDefinition.WizardPages.find((p) => p.Name == 'basetable');
        wizard.value?.goto(settingsPage!);

        // is it in inner wizard pages?
        baseTablePage.value?.goto(page);
    },
});
</script>
<template>
    <ContentComp
        :loadState="LoadState"
        :showAsModal="true"
        :fill-content="true"
        class="h-100 p-0"
    >
        <WizardComp
            v-if="event != null"
            :wizardDefinition="wizardDefinition"
            :showPrevNext="false"
            :show-header="false"
            :useGivenSpace="true"
            :startOnPageIndex="props.hasBibAssignRules ? 1 : 0"
            ref="wizard"
            class="h-100"
            @tabShown="tabShown"
        >
            <!-- Asign by category or group. This will auto assign priority -->
            <template #wizardPage-categorygroupselect>
                <div class="text-center h-100 d-flex flex-column justify-content-center">
                    <span
                        class="display-3"
                        v-html="$tm('bibassign.assignbyquestion', '', true)"
                    ></span>
                    <small>
                        {{ $tm('bibassign.assignbyinfo') }}
                        {{ event.Setup!.CategoryControlled ? $tm('categories.categories') : $tm('group.groups') }}
                    </small>
                    <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                        <ButtonComp
                            :button-type="event.Setup!.CategoryControlled ? 'Primary' : 'Secondary'"
                            class="btn-lg me-2"
                            :icon="featureIcons.CATEGORY"
                            @click="assignByCategory(true)"
                        >
                            {{ $tm('categories.categories') }}
                        </ButtonComp>
                        <ButtonComp
                            :button-type="!event.Setup!.CategoryControlled ? 'Primary' : 'Secondary'"
                            class="btn-lg"
                            :icon="featureIcons.GROUPS"
                            @click="assignByCategory(false)"
                        >
                            {{ $tm('group.groups') }}
                        </ButtonComp>
                    </div>
                </div>
            </template>

            <!-- Here we assign reserve bibs for either categories or groups -->
            <template #wizardPage-assignreservebibs>
                <div class="text-center h-100 d-flex flex-column justify-content-center">
                    <template v-if="event.Setup?.CategoryControlled">
                        <span
                            class="display-3"
                            v-html="$tm('bibassign.reservebibsforcategory', '', true)"
                        ></span>
                        <div class="d-flex justify-content-center mb-5 pb-5 bt-5 pt-5 gap-5">
                            <span class="pe-4">
                                <InputInt
                                    class="form-control-lg w-25 text-center mx-auto"
                                    icon=""
                                    posttext=""
                                    v-model="numberOfReserveBibsPrCategory"
                                />
                            </span>
                        </div>
                    </template>
                    <template v-else>
                        <span
                            class="display-3 pt-5 mt-5"
                            v-html="$tm('bibassign.reservebibsforgroup', '', true)"
                        ></span>
                        <div class="d-flex justify-content-center mb-3 bt-5 gap-5">
                            <span class="pe-4">
                                <InputInt
                                    class="form-control-lg w-25 text-center mx-auto"
                                    icon=""
                                    posttext=""
                                    v-model="numberOfReserveBibsPrGroup"
                                />
                            </span>
                        </div>
                    </template>
                    <div class="d-flex justify-content-center mb-5 pb-5 bt-5 pt-5 gap-5">
                        <span class="pe-4">
                            <InputBool
                                label="bibassign.reservebibsbefore"
                                class="form-checkbox-lg display-3"
                                v-model="assignReserveBibsBefore"
                            />
                        </span>
                    </div>

                    <div class="d-flex justify-content-center mb-3 bt-5 pt-5 gap-5">
                        <ButtonComp
                            button-type="Primary"
                            class="btn-lg"
                            icon="fa-check"
                            @click="assignReserveBibsForCategoriesAndGroups"
                        >
                            {{ $tm('button.ok') }}
                        </ButtonComp>
                    </div>
                </div>
            </template>

            <!-- Show the base table wizard -->
            <template #wizardPage-basetable>
                <BibAssignBaseTableView
                    class="h-100"
                    v-model="event"
                    v-model:categories="categories"
                    v-model:groups="groups"
                    v-model:blockedBibs="blockedBibs"
                    v-model:bibAssignSort="bibAssignSort"
                    ref="baseTablePage"
                    :saveCallback="async () => await save()"
                    :has-bib-assign-rules="props.hasBibAssignRules"
                    @assignable="(canAssign: boolean) => emit('assignable', canAssign)"
                    @isTablePage="(state: boolean) => emit('isTablePage', state)"
                    @isSeedingPage="(state: boolean) => emit('isSeedingPage', state)"
                    @hasChanges="(state: boolean) => emit('hasChanges', state)"
                    @assigned="$emit('assigned')"
                    @leftPage="(page: string) => emit('leftPage', page)"
                />
            </template>
        </WizardComp>
    </ContentComp>
</template>
